// src/services/aeo/deliveryMock.ts
//
// The Delivery sample store: what the Delivery routes will return, served on
// the sample business only, and only where Oura itself runs on fixtures
// (every local run). Staging and production answer notServed until Ipul's
// routes ship. DELETE with the other sample stores once they do.
//
// Deliverables come from the contract (the demo engagement, as staff edit
// it), one per code per plan month, S2 and S4 one per outlet. Their items
// follow the catalogue (the proposal's appendix). For nyonya-table a scenario
// sets where each item stands today, dated relative to today: Foundation ran
// over, so a few month 1 items are late or came back, and month 2 is under
// way. Every other month starts Later.
//
// Dev tools, in the console of a local run:
//   window.__ouraResetDelivery()   reseed the sample from scratch
//
// Store v4 (contract v1.3, 2 Oct 2026) adds the Phase 3 item fields on 4
// nyonya-table items (a draft a person started from the Answer Plan, a paste
// list, a "Live since" reply drafted for a person, the AI reader checks) and
// G4's next step as its unshared doc, so `create_doc` reads "Read it, then
// share" behind the Docs write switch.
//
// Lane 5 (2 Oct 2026): G1's step this month is its Monthly Work Snapshot, which
// no doc covers yet ("Create the doc"), patched onto saved v4 stores on
// read. Every stored step follows the docs sample store on each read
// (`withDocStep`): a doc made for it turns Create into "Read it, then
// share", and sharing it clears the step, so Create is never offered twice.
//
// Lane 6 (2 Oct 2026, Phase 3): `seedWork` adds nyonya-table's work on
// items once per store (fix drafts a person started, the signature page's
// draft from the client's answer, its copy as a download on Wix, the AI reader
// test before the robots fix), and the store answers the paste-step ticks, a
// proof file on Close by hand, a draft page from approved copy and the AI
// reader test. In the console, `window.__ouraPublishSampleDraft(id)` does
// what Site issues' publish will do once H34 ships: the fix counts as Mark
// fixed, and Check now closes it.
//
// The status rules live in deliveryRules.ts; this file only stores parts and
// applies the staff actions to them.

import { sampleActor } from "./sampleActor";
import type {
    CheckNowResult,
    CopyToSite,
    CopyToSiteMode,
    Deliverable,
    DeliveryDocument,
    DocumentWait,
    DeliveryItem,
    DeliveryPayload,
    DeliveryResult,
    DeliverySuggestion,
    HistoryEntry,
    ItemPage,
    KeyPage,
    KeyPages,
    LedgerCounts,
    LedgerRow,
    PageIn,
    ProofIn,
    ReaderCheck,
    SlotIn,
    TickIn,
    TodayPayload,
    TodayRow,
    SuggestionsPayload,
} from "./deliveryTypes";
import { deliveryFailure } from "./deliveryTypes";
import { AI_READERS, GROUP_ORDER, KEY_PAGE_CODES, computeDeliverable, readerItemOf, dayLabel, doneCountOf, settleItem, shortDay, todayRank } from "./deliveryRules";
import { activeDocExpected, addDays, addMonths, expectedDocFor, lastWorkingDayOnOrBefore, myDate, myInstant, shiftWorkingDays, termMonth } from "./clientDocsRules";
import type { Engagement } from "./clientReview";
import { getBusiness, getSnapshot } from "../../oura/data";
import { getTracker } from "../../oura/tracker";
import { docsStoreApi } from "./clientDocsMock";
import type { BusinessDocuments } from "./clientDocsTypes";
import { DOCS_WRITE_ON } from "../../oura/docs/writeFlag";

/**
 * Whether this store answers: only on a local fixtures run (the dev server,
 * Oura on fixtures, the Delivery switch off). A build never serves it, so a
 * build missing its switches answers `notServed` instead of sample data.
 */
export const DELIVERY_SAMPLE_SERVES =
    import.meta.env.DEV &&
    import.meta.env.VITE_ENABLE_OURA_DELIVERY_LIVE !== "true" &&
    import.meta.env.VITE_ENABLE_OURA_LIVE_API !== "true";

const STORE_KEY = "__oura_delivery_store_v5__";
const SAMPLE_ID = "nyonya-table";
const MANAGER = "Angel";
/** The person an action records: the signed-in person on the dev server, else the seed's manager. */
const me = () => sampleActor(MANAGER);

/* ------------------------------------------------------------------------- *
 * The store
 * ------------------------------------------------------------------------- */

interface BizState {
    /** Stored parts; the computed fields are filled on every read. */
    deliverables: Deliverable[];
    keyPages: KeyPages;
    ledgerRows: LedgerRow[];
    lastCheck: { at: string | null; kind: "scan" | "recheck" | null; running: boolean };
    /** The last scan's suggestions and every decision (H11). */
    suggestions?: DeliverySuggestion[];
    /** The contract signature the deliverables were built from; a plan edit rebuilds them. */
    planSig: string;
    /** Lane 6: the Phase 3 work seeded on nyonya-table (once, on fresh and saved v4 stores alike). */
    workSeed?: number;
}

interface Store {
    seededOn: string;
    businesses: Record<string, BizState>;
}

let store: Store = load();
const listeners = new Set<() => void>();

function load(): Store {
    try {
        const raw = window.localStorage.getItem(STORE_KEY);
        if (raw) return JSON.parse(raw) as Store;
    } catch {
        /* private mode, or a stale shape: start clean */
    }
    return { seededOn: today(), businesses: {} };
}

function save(): void {
    try {
        window.localStorage.setItem(STORE_KEY, JSON.stringify(store));
    } catch {
        /* ignore */
    }
}

/** Be told after any sample write (including a Check now that finishes later). */
export function setDeliveryMockListener(cb: () => void): () => void {
    listeners.add(cb);
    return () => {
        listeners.delete(cb);
    };
}

function emit(): void {
    Array.from(listeners).forEach((cb) => cb());
}

export function resetDeliveryStore(): void {
    store = { seededOn: today(), businesses: {} };
    save();
    emit();
}

if (typeof window !== "undefined" && DELIVERY_SAMPLE_SERVES) {
    (window as unknown as Record<string, unknown>).__ouraResetDelivery = resetDeliveryStore;
}

function today(): string {
    return myDate(new Date());
}

function now(): string {
    return new Date().toISOString();
}

function clone<T>(v: T): T {
    return JSON.parse(JSON.stringify(v)) as T;
}

function respond<T>(work: () => DeliveryResult<T>, ms = 160): Promise<DeliveryResult<T>> {
    return new Promise((resolve) => {
        window.setTimeout(() => {
            const out = work();
            if (out.ok) save();
            resolve(out.ok ? { ok: true, data: clone(out.data) } : out);
        }, ms);
    });
}

/* ------------------------------------------------------------------------- *
 * Dates
 * ------------------------------------------------------------------------- */

/** n working days before today (n > 0). */
const wd = (n: number) => shiftWorkingDays(today(), n);
/** n calendar days before today. */
const cal = (n: number) => addDays(today(), -n);
/** A Malaysian instant on a date. */
const at = (ymd: string, hour = 10, minute = 0) => myInstant(ymd, hour, minute);

function windowOf(start: string, month: number): { start: string; end: string } {
    const s = addMonths(start, month - 1);
    const e = addDays(addMonths(start, month), -1);
    return { start: s, end: e };
}

/* ------------------------------------------------------------------------- *
 * The catalogue's items, per code (the proposal's appendix)
 * ------------------------------------------------------------------------- */

interface ItemTemplate {
    part: string;
    kind: DeliveryItem["kind"];
    label: string;
    technical?: string;
    checkKey?: string;
    scope?: DeliveryItem["scope"];
    staffDo?: string;
}

const CHECK_WORDS: Record<string, { label: string; technical: string; staffDo: string }> = {
    broken_4xx: { label: "Pages that will not open", technical: "Broken pages (4xx)", staffDo: "Fix or redirect them, then Mark fixed" },
    broken_outbound: { label: "Dead links to other sites", technical: "Links to pages that no longer exist", staffDo: "Remove or replace the links, then Mark fixed" },
    redirect_chains: { label: "Old links that hop more than once", technical: "Redirect chains", staffDo: "Point the redirects straight at the pages, then Mark fixed" },
    canonicals: { label: "Main address tags on the key pages", technical: "Canonical issues", staffDo: "Fix the tags, then Mark fixed" },
    missing_meta: { label: "Summary lines", technical: "Missing meta description", staffDo: "Write them, then Mark fixed" },
    dup_titles: { label: "Page names", technical: "Duplicate title tags", staffDo: "Rename them, then Mark fixed" },
    missing_alt: { label: "Image descriptions", technical: "Images missing alt text", staffDo: "Describe them, then Mark fixed" },
    missing_h1: { label: "Main headings", technical: "Missing page heading", staffDo: "Add them, then Mark fixed" },
    orphan_pages: { label: "Links to the key pages", technical: "Orphan pages", staffDo: "Link to them from related pages, then Mark fixed" },
    cwv: { label: "Speed on a phone", technical: "Core Web Vitals, from PageSpeed", staffDo: "Speed them up, then Mark fixed" },
    no_schema: { label: "Structured data", technical: "Missing structured data", staffDo: "Add it, then Mark fixed" },
    schema_no_entity: { label: "The business named in the markup", technical: "Structured data says nothing about the business", staffDo: "Name it, then Mark fixed" },
    no_price_signal: { label: "Prices a machine can read", technical: "No price a machine can read", staffDo: "Add the prices, then Mark fixed" },
    staff_unattributed: { label: "Practitioner pages linked to the business", technical: "Staff pages not linked to the business", staffDo: "Link them in the markup, then Mark fixed" },
    branch_records_missing: { label: "A business record for every branch", technical: "One business record for several branches", staffDo: "Add the records, then Mark fixed" },
    robots_missing: { label: "The robots file", technical: "robots.txt missing", staffDo: "Add it, then Mark fixed" },
    thin_content: { label: "Enough words on the key pages", technical: "Thin content", staffDo: "Add substance, then Mark fixed" },
};

const WHOLE_SITE = new Set(["broken_4xx", "broken_outbound", "redirect_chains"]);
const SITE_WIDE = new Set(["robots_missing", "branch_records_missing"]);

function check(key: string): ItemTemplate {
    const w = CHECK_WORDS[key] ?? { label: key, technical: key, staffDo: "Fix it, then Mark fixed" };
    return {
        part: key,
        kind: "check",
        checkKey: key,
        label: w.label,
        technical: w.technical,
        staffDo: w.staffDo,
        scope: WHOLE_SITE.has(key) ? "whole_site" : SITE_WIDE.has(key) ? "site" : "key_pages",
    };
}

/** Item ids follow H4 and H5: a check key, `tick-{ask}`, `outlet-{slug}`, `record-{name}` or `slot-{n}`. */
function templatesFor(code: string, covers: readonly string[], lineLabel = ""): ItemTemplate[] {
    switch (code) {
        case "A1":
            return [{ part: "record-baseline", kind: "record", label: "The scored baseline run", technical: "A run Oura stores", staffDo: "Run the assessment" }];
        case "S5":
            return [
                { part: "record-connected", kind: "record", label: "Search Console connected", technical: "The connection", staffDo: "Connect it with the client" },
                { part: "record-first_data", kind: "record", label: "First Search Console data", technical: "Google's first day of data", staffDo: "Nothing to do" },
                { part: "tick-website_login", kind: "confirm", label: "Website login given", staffDo: "Check the login works, then tick" },
            ];
        case "S3":
            return ["broken_4xx", "broken_outbound", "redirect_chains", "canonicals"].map(check);
        case "S1":
            return ["missing_meta", "dup_titles", "missing_alt", "missing_h1"].map(check);
        case "S6":
            return [check("orphan_pages")];
        case "S7":
            return [check("cwv")];
        case "A2":
            return ["no_schema", "schema_no_entity", "no_price_signal", "staff_unattributed", "branch_records_missing"].map(check);
        case "A3":
            return [check("robots_missing"), { part: "tick-ai_readers", kind: "confirm", label: "All 3 AI readers get in", staffDo: "Test, then tick" }];
        case "S2":
            return [{ part: "tick-google_profile", kind: "confirm", label: "The outlet's Google profile", staffDo: "Claim and fix the profile, then tick with its link" }];
        case "S4":
            return [{ part: "tick-listings", kind: "confirm", label: "The outlet's listings match across the web", staffDo: "Fix the listings, then tick" }];
        case "A4":
            return [
                { part: "record-rescore", kind: "record", label: "The re-score run", technical: "The last full run before the handover date", staffDo: "Run the assessment" },
                { part: "tick-handover", kind: "confirm", label: "Handover held, with the scorecard", staffDo: "Hold the call, then tick with the scorecard link" },
                { part: "tick-revision_round", kind: "confirm", label: "The revision round", staffDo: "Tick when it is used" },
            ];
        case "G2":
            return ["broken_4xx", "redirect_chains", "canonicals", "thin_content"].map(check);
        case "G3":
            return [{ part: "tick-scoreboard", kind: "confirm", label: "This month's scoreboard", staffDo: "Share it, then tick with its link" }];
        case "G4":
            return [{ part: "tick-competitor_watch", kind: "confirm", label: "This month's competitor watch", staffDo: "Review the 5 rivals, then tick" }];
        case "G5":
            return [{ part: "tick-small_change", kind: "confirm", label: "This month's small change", staffDo: "Make the change, then tick" }];
        case "G6":
            return [{ part: "record-report", kind: "record", label: "This month's report", technical: "The stored report", staffDo: "Send the report" }];
        case "G7":
            return [{ part: "record-reading", kind: "record", label: "This month's ChatGPT app reading", technical: "The stored reading", staffDo: "Run the capture" }];
        case "G8":
            return [{ part: "tick-term_review", kind: "confirm", label: "The term review held", staffDo: "Hold it, then tick" }];
        default:
            // A custom line from Site issues covers pages beyond the key pages: the check reads the whole site.
            if (/^X\d+$/.test(code) && covers.length > 0) return covers.map((k) => ({ ...check(k), label: check(k).label.replace(/ on the key pages$/, ""), scope: "whole_site" as const }));
            return [{ part: "tick-custom", kind: "confirm", label: lineLabel || "The custom work", staffDo: "Do it, then tick" }];
    }
}

/** The leading number in a quantity: "10 pages, 2 of them Signature" is 10, "3 a month" is 3. */
function leadingNumber(q: string | null | undefined): number | null {
    const m = (q ?? "").match(/^\s*(\d+)/);
    return m ? Number(m[1]) : null;
}

function signatureCount(q: string | null | undefined): number {
    const m = (q ?? "").match(/(\d+)\s+of them Signature/i);
    return m ? Number(m[1]) : 0;
}

/* ------------------------------------------------------------------------- *
 * Building deliverables from the contract
 * ------------------------------------------------------------------------- */

type Line = Engagement["deliverables"][number];

function planSig(e: Engagement): string {
    return JSON.stringify([e.startedAt, e.termMonths, e.outlets, e.deliverables.map((d) => [d.code, d.months, d.quantity, d.covers])]);
}

function outletNames(businessId: string, n: number): string[] {
    const known = businessId === SAMPLE_ID ? ["Bukit Bintang"] : [];
    return Array.from({ length: Math.max(1, n) }, (_, i) => known[i] ?? `Outlet ${i + 1}`);
}

function blankItem(key: string, t: ItemTemplate, due: string): DeliveryItem {
    return {
        id: `${key}:${t.part}`,
        kind: t.kind,
        checkKey: t.checkKey ?? null,
        label: t.label,
        technical: t.technical ?? null,
        scope: t.scope ?? null,
        state: "todo",
        waitingOn: null,
        cameBack: false,
        due,
        pages: [],
        staffDo: t.staffDo ?? null,
        forcedDone: null,
    };
}

function slotItems(key: string, n: number, signature: number, due: string, code: string): DeliveryItem[] {
    return Array.from({ length: n }, (_, i) => ({
        id: `${key}:slot-${i + 1}`,
        kind: "page" as const,
        label: `Page ${i + 1}`,
        scope: null,
        state: "todo" as const,
        waitingOn: null,
        cameBack: false,
        due,
        pages: [],
        slot: { index: i + 1, topic: null, pageKind: null, signature: code === "B1" && i < signature },
        staffDo: "Agree new or existing, write or improve it, publish, then add the live page",
    }));
}

/** H5's outlet slug: lowercase, runs of anything else to "-", at most 40 characters. */
function outletSlug(name: string): string {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40) || "outlet";
}

function buildDeliverables(businessId: string, e: Engagement): Deliverable[] {
    /**
     * NO START DATE MEANS NO DATES (H7, ourteam-backend#5162, #3967).
     *
     * The sample store used to fall back to today's date here, so every
     * deliverable it made carried a window and a due even for an engagement
     * that had never started — the one shape the server answers with nulls.
     * That is why the crash reached staging: the fixtures agreed with the
     * types, and both were wrong, so no local run could reproduce it.
     *
     * `start` is still needed to lay the months out; `dated` is what decides
     * whether those dates are shown or sent as null.
     */
    const dated = !!e.startedAt;
    const start = (e.startedAt ?? today()).slice(0, 10);
    const out: Deliverable[] = [];
    const outlets = outletNames(businessId, e.outlets);
    const many = outlets.length > 1;
    e.deliverables.forEach((line: Line) => {
        const code = line.code.trim().toUpperCase();
        line.months.forEach((m) => {
            const win = windowOf(start, m);
            const due = lastWorkingDayOnOrBefore(win.end);
            // H5: S2 is one deliverable per outlet once 2 or more are sold; S4 is one
            // deliverable with an item per outlet. One outlet needs no names in keys.
            const targets: Array<string | null> = code === "S2" && many ? outlets : [null];
            const single = !many && (code === "S2" || code === "S4") ? outlets[0] : null;
            targets.forEach((outlet) => {
                const key = outlet ? `${code}-m${m}-${outletSlug(outlet)}` : `${code}-m${m}`;
                const per = leadingNumber(line.quantity);
                // H16: a custom line with no covers sells pages when its words say "page" and its
                // quantity starts with a number: that many slots each month when monthly, else all
                // in its first month.
                const sellsPages = /^X\d+$/.test(code) && !(line.covers ?? []).length && per != null && /\bpages?\b/i.test(`${line.label} ${line.quantity ?? ""} ${line.note ?? ""}`);
                const monthlyLine = line.cadence === "monthly" || /\b(a|per) month\b/i.test(line.quantity ?? "");
                const pageSlots = sellsPages && (monthlyLine || m === Math.min(...line.months)) ? per : null;
                const items =
                    code === "B1" || code === "G1"
                        ? slotItems(key, per ?? (code === "B1" ? 8 : 1), signatureCount(line.quantity), due, code)
                        : pageSlots
                          ? slotItems(key, pageSlots, 0, due, code)
                        : code === "S4" && many
                          ? outlets.map((o) => ({ ...blankItem(key, { part: `outlet-${outletSlug(o)}`, kind: "confirm", label: `${o}: listings match across the web`, staffDo: "Fix the listings, then tick" }, due), outlet: o }))
                          : templatesFor(code, line.covers ?? [], line.label).map((t) => ({ ...blankItem(key, t, due), outlet: outlet ?? single }));
                out.push({
                    key,
                    code,
                    label: outlet ? `${line.label}, ${outlet}` : line.label,
                    month: m,
                    window: dated ? win : null,
                    due: dated ? due : null,
                    group: "later",
                    next: null,
                    actor: null,
                    head: "",
                    clientWord: "Not started",
                    clientLine: "",
                    items,
                    document: null,
                    mainAction: { kind: "next_deliverable", label: "Next deliverable" },
                    history: [],
                    reportedByHand: false,
                    outlet: outlet ?? single,
                    extras: null,
                });
            });
        });
    });
    return out;
}

/* ------------------------------------------------------------------------- *
 * The sample scenario: nyonya-table, today
 * ------------------------------------------------------------------------- */

const KEY_PAGE_URLS = ["/", "/menu", "/locations/bukit-bintang", "/private-dining", "/catering", "/faqs", "/booking", "/about", "/contact", "/gallery"];

function sampleKeyPages(): KeyPages {
    const clicks: Record<string, number> = { "/": 188, "/menu": 74, "/locations/bukit-bintang": 61, "/private-dining": 29, "/catering": 22, "/faqs": 17, "/booking": 15, "/about": 8 };
    const inlinks: Record<string, number> = { "/contact": 31, "/gallery": 18, "/gift-vouchers": 9, "/blog/nyonya-kueh-guide": 6, "/events": 4 };
    const titles: Record<string, string> = {
        "/": "Homepage", "/menu": "Menu and prices", "/locations/bukit-bintang": "Bukit Bintang", "/private-dining": "Private dining", "/catering": "Catering",
        "/faqs": "FAQs", "/booking": "Reservations", "/about": "About us", "/contact": "Contact", "/gallery": "Gallery", "/gift-vouchers": "Gift vouchers",
        "/blog/nyonya-kueh-guide": "A guide to nyonya kueh", "/events": "Events",
    };
    const page = (url: string): KeyPage => ({
        url,
        source: url === "/" ? "home" : clicks[url] ? "clicks" : "links",
        clicks: clicks[url] ?? null,
        inlinks: inlinks[url] ?? null,
        title: titles[url] ?? null,
    });
    const suggested = KEY_PAGE_URLS.map(page);
    return {
        pages: suggested,
        suggested,
        candidates: ["/gift-vouchers", "/blog/nyonya-kueh-guide", "/events"].map(page),
        confirmed: false,
        confirmedAt: null,
        confirmedBy: null,
    };
}

const P = (url: string, state: ItemPage["state"], detail?: string | null, extra: Partial<ItemPage> = {}): ItemPage => ({ url, state, detail: detail ?? null, ...extra });

function doc(docId: string, title: string, state: string, repliedAt: string | null = null, wait: Partial<DeliveryDocument> = {}): DeliveryDocument {
    return { docId, title, state, repliedAt, stateKey: null, waitingOn: null, waitingSince: null, waitingWorkingDays: null, late: false, lateWorkingDays: null, replyBy: null, ...wait };
}

function h(ymd: string, text: string, area: HistoryEntry["area"], by: string | null = null, hour = 10): HistoryEntry {
    return { at: at(ymd, hour), text, area, by };
}

/** Where each nyonya-table item stands today. Months before the current one that are not named here close as done. */
function applyScenario(list: Deliverable[], start: string, current: number): void {
    const byKey = new Map(list.map((d): [string, Deliverable] => [d.key, d]));
    const get = (key: string) => byKey.get(key);
    const item = (d: Deliverable | undefined, part: string) => d?.items.find((i) => i.id === `${d.key}:${part}`);
    const m1 = windowOf(start, 1);
    const scan1 = addDays(m1.end, -5); // the Foundation's last full scan

    // Months before the current one close as done by default, dated inside their window,
    // except the deliverables the scenario below sets itself.
    const scripted = new Set(["A1-m1", "S5-m1", "S3-m1", "S1-m1", "S6-m1", "S7-m1", "A2-m1", "A3-m1", "B1-m1", "A4-m1", "G7-m1"]);
    list.forEach((d) => {
        if (d.month >= current || scripted.has(d.key) || (d.month === 1 && (d.code === "S2" || d.code === "S4"))) return;
        d.items.forEach((i, k) => {
    const when = addDays(d.window!.end, -3 - (k % 3));
            i.state = "done";
            i.proof = i.proof ?? { kind: i.kind === "confirm" ? "note" : i.kind === "page" ? "live" : "scan", text: i.kind === "confirm" ? `Ticked ${shortDay(when)}` : `Confirmed ${shortDay(when)}`, at: at(when) };
        });
        d.doneAt = addDays(d.window!.end, -2);
    });

    const A1 = get("A1-m1");
    if (A1) {
        const run = item(A1, "record-baseline")!;
        run.state = "done";
        run.proof = { kind: "run", text: "Baseline run, 50 questions scored", at: at(addDays(start, 1)) };
        A1.doneAt = addDays(start, 1);
        A1.proofLine = "Your 50 questions are locked and scored.";
        A1.history = [h(addDays(start, 1), "Baseline run stored: 50 questions scored", "scan")];
    }

    const S5 = get("S5-m1");
    if (S5) {
        Object.assign(item(S5, "record-connected")!, { state: "done", proof: { kind: "connection", text: "Connected", at: at(addDays(start, 15)) } });
        Object.assign(item(S5, "record-first_data")!, { state: "done", proof: { kind: "connection", text: "First data arrived", at: at(addDays(start, 16)) } });
        Object.assign(item(S5, "tick-website_login")!, { state: "done", proof: { kind: "note", text: "Admin login works", by: "Dana", at: at(addDays(start, 10)) } });
        S5.doneAt = addDays(start, 16);
        S5.proofLine = "Search Console is connected and recording.";
        S5.history = [h(addDays(start, 16), "First Search Console data arrived", "scan"), h(addDays(start, 15), "Search Console connected", "plan", MANAGER), h(addDays(start, 10), "Website login ticked: Admin login works", "tick", "Dana")];
    }

    const S3 = get("S3-m1");
    if (S3) {
        item(S3, "broken_4xx")!.pages = ["/old-menu", "/promo-2024", "/events/cny-2025", "/careers"].map((u) => P(u, "done", "Returned 404"));
        item(S3, "broken_outbound")!.pages = [P("/blog/best-laksa", "done", "1 dead link, re-checked"), P("/about", "done", "1 dead link, re-checked"), P("/faqs", "done", "1 dead link, re-checked")];
        item(S3, "redirect_chains")!.pages = [P("/menu-2023", "done", "2 hops"), P("/reservations", "done", "3 hops")];
        item(S3, "canonicals")!.pages = [];
        S3.items.forEach((i) => (i.readAt = at(scan1)));
        S3.doneAt = scan1;
        S3.proofLine = "Our scan found 0 broken pages or dead links. 9 fixed since the start.";
        S3.extras = { dismissedSuggestions: [{ text: "Delete 3 unfinished pages", by: "YW", at: at(addDays(start, 14)) }] };
        S3.history = [
            h(scan1, "Scan: S3 checks 9 to 0", "scan"),
            h(addDays(scan1, -3), `${MANAGER} fixed 3 dead links on the site`, "mark", MANAGER),
            h(addDays(start, 14), "YW dismissed Delete 3 unfinished pages", "plan", "YW"),
            h(addDays(start, 1), "First crawl: 146 pages", "scan"),
        ];
    }

    const S1 = get("S1-m1");
    if (S1) {
        item(S1, "missing_meta")!.pages = ["/", "/menu", "/private-dining", "/faqs", "/booking", "/contact"].map((u) => P(u, "waiting", null, { markedFixedAt: at(wd(1), 16) }));
        item(S1, "missing_meta")!.markedFixedAt = at(wd(1), 16);
        item(S1, "dup_titles")!.pages = ["/", "/menu", "/catering", "/private-dining", "/about", "/gallery", "/contact"].map((u) => P(u, "done", "Clear since the last scan"));
        item(S1, "missing_alt")!.pages = [P("/menu", "todo", "5 of 9 images"), P("/about", "todo", "3 of 3 images"), P("/gallery", "todo", "2 of 4 images"), P("/contact", "todo", "2 of 2 images"), P("/", "todo", "2 of 7 images")];
        item(S1, "missing_alt")!.nextText = "Describe 14 images on 5 key pages";
        item(S1, "missing_h1")!.pages = [P("/booking", "todo"), P("/catering", "todo"), P("/faqs", "todo")];
        item(S1, "missing_h1")!.nextText = "Add main headings to 3 pages";
        // Phase 3 (H35): Oura cannot publish headings on this site, so the change is a paste list.
        const headings: Array<[string, string, string]> = [
            ["/booking", "Booking", "Book a table at Nyonya Table"],
            ["/catering", "Catering", "Nyonya catering for offices and parties"],
            ["/faqs", "FAQs", "Questions about Nyonya Table"],
        ];
        item(S1, "missing_h1")!.pasteList = {
            platform: "wix",
            steps: headings.map(([url, page, words], i) => ({
                id: `paste-${i + 1}`,
                page: url,
                where: `Pages, then ${page}, then the first text box, then Heading 1`,
                words,
                ticked: i === 0,
                tickedBy: i === 0 ? "Dana" : null,
                tickedAt: i === 0 ? at(wd(1), 15) : null,
            })),
            allTicked: false,
            forDeveloper: headings.map(([url, , words]) => `${url}: set the page's main heading (H1) to "${words}"`).join("\n"),
        };
        // Phase 3 (H37): the page names went live; the reply is drafted for a person to post in the doc that approved them.
        const titlesThread = docsStoreApi.findDoc("doc_f13_page_titles")?.f.threads?.[0]?.id ?? null;
        if (titlesThread) {
            item(S1, "dup_titles")!.liveSinceDraft = {
                text: `Live since ${shortDay(scan1)}: the page titles you approved are on ${item(S1, "dup_titles")!.pages.length} pages of your site.`,
                docId: "doc_f13_page_titles",
                threadId: titlesThread,
                madeAt: at(scan1, 9),
                postedAt: null,
                postedBy: null,
            };
        }
        S1.document = doc("doc_f13_page_titles", "Page Titles Review (S1)", "Shared with the client", null, { stateKey: "nothing_asked" });
        S1.history = [
            h(wd(1), `${MANAGER} marked summary lines fixed on 6 pages`, "mark", MANAGER, 16),
            h(scan1, "Scan: page names on 7 pages clear", "scan"),
            h(addDays(start, 2), "Carried from Fixes: Optimise page labels", "carried"),
        ];
    }

    // Month 2's sweep found nothing to fix and nobody logged work on it: it passed on its own (Jack, 22 Oct 2026).
    const G2m2 = current > 2 ? get("G2-m2") : undefined;
    if (G2m2?.window) G2m2.passedWithoutWork = { at: at(addDays(G2m2.window.start, 3)) };

    const S6 = get("S6-m1");
    if (S6) {
        item(S6, "orphan_pages")!.pages = [
            P("/private-dining", "done", "Linked from 3 pages"), P("/catering", "done", "Linked from 2 pages"), P("/gallery", "done", "Linked from the menu"), P("/faqs", "done", "Linked from the footer"),
            P("/booking", "waiting", null, { markedFixedAt: at(cal(2), 15) }), P("/about", "waiting", null, { markedFixedAt: at(cal(2), 15) }),
        ];
        item(S6, "orphan_pages")!.markedFixedAt = at(cal(2), 15);
        S6.history = [h(cal(2), `${MANAGER} marked links to 2 key pages fixed`, "mark", MANAGER, 15), h(scan1, "Scan: 4 of 6 key pages linked", "scan")];
    }

    const S7 = get("S7-m1");
    if (S7) {
        item(S7, "cwv")!.pages = [
            P("/menu", "todo", "4.1 s on a phone"), P("/catering", "todo", "3.8 s on a phone"),
            P("/", "done", "1.9 s, passes"), P("/locations/bukit-bintang", "done", "2.1 s, passes"), P("/private-dining", "done", "2.3 s, passes"),
            P("/faqs", "not_read", "Not measured yet"), P("/booking", "not_read", "Not measured yet"), P("/about", "not_read", "Not measured yet"), P("/contact", "not_read", "Not measured yet"), P("/gallery", "not_read", "PageSpeed could not load it"),
        ];
        item(S7, "cwv")!.nextText = "Speed up /menu and /catering";
        S7.history = [h(scan1, "PageSpeed: 3 of 5 measured pages pass", "scan")];
    }

    const A2 = get("A2-m1");
    if (A2) {
        item(A2, "no_schema")!.pages = [P("/menu", "todo", "Markup gone since the last scan", { cameBack: true }), P("/", "done", "Restaurant markup"), P("/locations/bukit-bintang", "done", "Restaurant markup")];
        item(A2, "no_schema")!.nextText = "Put the markup back on /menu";
        item(A2, "schema_no_entity")!.pages = [P("/", "done", "Clear since the last scan")];
        item(A2, "no_price_signal")!.pages = [P("/menu", "not_read", "Not read on the last scan")];
        item(A2, "staff_unattributed")!.doesNotApply = "No practitioner pages";
        item(A2, "branch_records_missing")!.pages = [];
        A2.items.forEach((i) => (i.readAt = at(scan1)));
        A2.history = [h(scan1, "Scan: structured data on /menu came back missing", "scan"), h(addDays(scan1, -6), `${MANAGER} added restaurant markup on 3 pages`, "mark", MANAGER)];
    }

    const A3 = get("A3-m1");
    if (A3) {
        item(A3, "robots_missing")!.pages = [P("/robots.txt", "waiting", null, { markedFixedAt: at(wd(1), 11) })];
        item(A3, "robots_missing")!.markedFixedAt = at(wd(1), 11);
        item(A3, "robots_missing")!.impact = `ClaudeBot was blocked by robots.txt until the fix on ${shortDay(myDate(at(wd(1), 11)))}, so Claude's answers could not read the site before then. ChatGPT and Perplexity were never blocked.`;
        Object.assign(item(A3, "tick-ai_readers")!, { state: "done", proof: { kind: "note", text: "All 3 allowed: GPTBot, Google-Extended, PerplexityBot", by: MANAGER, at: at(addDays(start, 12)) } });
        // Phase 3 (H38): the AI reader test, 1 line per reader, run on the homepage.
        item(A3, "tick-ai_readers")!.readerChecks = [
            { reader: "chatgpt", agent: "GPTBot", robotsAllows: true, gotIn: true, wordsVisible: true, checkedAt: at(wd(1), 2) },
            { reader: "claude", agent: "ClaudeBot", robotsAllows: true, gotIn: true, wordsVisible: true, checkedAt: at(wd(1), 2) },
            { reader: "perplexity", agent: "PerplexityBot", robotsAllows: true, gotIn: true, wordsVisible: true, checkedAt: at(wd(1), 2) },
        ];
        A3.history = [h(wd(1), `${MANAGER} marked the robots file fixed`, "mark", MANAGER, 11), h(addDays(start, 12), "AI readers ticked: all 3 allowed", "tick", MANAGER)];
    }

    const B1 = get("B1-m1");
    if (B1) {
        const pages: Array<[string, "new" | "existing", string, string]> = [
            ["/", "existing", "Homepage", addDays(start, 15)], ["/prices", "existing", "Prices", addDays(start, 15)],
            ["/locations/bukit-bintang", "existing", "Bukit Bintang location", addDays(start, 17)], ["/faqs", "existing", "FAQs", addDays(start, 17)],
            ["/booking", "existing", "Reservations", addDays(start, 21)], ["/about", "existing", "About us", addDays(start, 21)],
            ["/private-dining", "existing", "Private dining", addDays(start, 24)], ["/catering", "existing", "Catering", addDays(start, 24)],
            ["/gift-vouchers", "new", "Gift vouchers", addDays(start, 28)], ["/contact", "existing", "Contact", addDays(start, 28)],
        ];
        B1.items.forEach((it, i) => {
            const p = pages[i];
            if (!p) return;
            it.state = "done";
            it.label = p[2];
            it.slot = { ...(it.slot ?? { index: i + 1 }), topic: p[2], pageKind: p[1], changed: p[1] === "existing" ? "Rewritten to answer the questions customers ask" : null };
            it.pages = [P(p[0], "done", p[1] === "new" ? "New page, read by the crawl" : "Improved, read by the crawl")];
            it.proof = { kind: "live", text: `Live ${shortDay(p[3])}, read by the scan`, at: at(p[3]) };
        });
        B1.doneAt = addDays(start, 28);
        B1.proofLine = `${B1.items.length} pages are live.`;
        B1.document = doc("doc_f6_copy_pack", "Copy Pack (B1)", "Approved", null, { stateKey: "approved" });
        B1.extras = { pagesSold: B1.items.length, pagesLive: B1.items.length, aiNamedSinceLive: { named: 4, of: 12 }, inDocument: "2 pages to strengthen and 3 more topics. The topics are offered for G1 from month 2." };
        B1.history = [h(addDays(start, 28), "Gift vouchers and Contact went live", "page", MANAGER), h(addDays(start, 24), "Private dining and Catering went live", "page", MANAGER), h(addDays(start, 15), "Homepage and Prices went live", "page", MANAGER)];
    }

    list.filter((d) => d.code === "S2" && d.month === 1).forEach((S2) => {
        Object.assign(S2.items[0], {
            state: "waiting",
            waitingOn: "client",
            askedClientAt: wd(6),
            clientAsk: `Give us owner access to the ${S2.outlet ?? ""} Google profile.`.replace("  ", " "),
            nextText: "Owner access to the Google profile",
        });
        S2.extras = { notes: [{ text: "Google verifies on its own clock, 1 to 2 weeks after access.", by: "Dana", at: at(wd(6)) }] };
        S2.history = [h(wd(6), "Asked the client for owner access", "reply", MANAGER)];
    });

    list.filter((d) => d.code === "S4" && d.month === 1).forEach((S4) => {
        Object.assign(S4.items[0], {
            state: "waiting",
            waitingOn: "client",
            askedClientAt: wd(4),
            clientAsk: `Send us the ${S4.outlet ?? ""} hours and phone in Business Details.`.replace("  ", " "),
            nextText: "Hours and phone, asked in Business Details",
        });
        // Asked 4 working days ago with 3 to reply: the question is 1 working day late.
        S4.document = doc("doc_f4_business_details", "Business Details (S4)", "Waiting on the client", null, {
            stateKey: "late", waitingOn: "client", waitingSince: at(wd(4)), waitingWorkingDays: 4, late: true, lateWorkingDays: 1, replyBy: wd(1),
        });
        S4.extras = { notes: [{ text: "Burpple, Chope and Yelp show the old number.", by: "Dana", at: at(wd(9)) }], movedOut: [{ text: "Get named elsewhere, 4 questions, now suggestions on Visibility", to: "visibility" }] };
        S4.history = [h(wd(4), "Asked for the hours and phone in Business Details", "reply", MANAGER)];
    });

    const A4 = get("A4-m1");
    if (A4) {
        Object.assign(item(A4, "record-rescore")!, { state: "done", proof: { kind: "run", text: "Re-score run stored", at: at(addDays(m1.end, -2)) } });
        Object.assign(item(A4, "tick-handover")!, { state: "done", proof: { kind: "link", text: "Scorecard", url: "https://docs.google.com/", by: MANAGER, at: at(m1.end, 15) } });
        Object.assign(item(A4, "tick-revision_round")!, { state: "done", proof: { kind: "note", text: "Not asked for at the handover", at: at(m1.end, 15) } });
        A4.doneAt = m1.end;
        A4.proofLine = "The handover call was held, with your scorecard.";
        A4.history = [h(m1.end, "Handover held, scorecard shared", "tick", MANAGER, 15), h(addDays(m1.end, -2), "Re-score run stored", "scan")];
    }

    const G7m1 = get("G7-m1");
    if (G7m1) {
        Object.assign(item(G7m1, "record-reading")!, { state: "done", proof: { kind: "reading", text: "ChatGPT app reading stored", at: at(addDays(m1.end, -5)) } });
        G7m1.doneAt = addDays(m1.end, -5);
        G7m1.proofLine = "This month's AI check is in your report.";
    }

    // The current month, under way.
    const c = current;
    const G1 = get(`G1-m${c}`);
    if (G1) {
        const [s1, s2, s3] = G1.items;
        if (s1) {
            Object.assign(s1, { label: "Your signature page", slot: { ...s1.slot!, topic: "Your signature page", pageKind: "new" }, pages: [P("/tasting-menu", "todo", "In draft")], clientRepliedAt: at(wd(2), 14) });
            // Phase 3 (H32, H33, H39): Angel started the page from the Answer Plan; Oura cannot create a page on Wix, so the copy is a download.
            s1.drafts = [
                {
                    id: "draft-tasting-menu",
                    kind: "page",
                    page: "/tasting-menu",
                    label: "Your signature page",
                    state: "draft",
                    startedBy: MANAGER,
                    startedAt: at(wd(1), 11),
                    fromQuestionId: null,
                    fromDocId: "doc_f1_answer_plan",
                    publishedAt: null,
                    publishedBy: null,
                    approvalKey: s1.id,
                },
            ];
            s1.copyToSite = { mode: "download", allowed: false, platform: "wix", reason: "Oura cannot create pages on Wix.", draftUrl: null, downloadUrl: null, docId: "doc_f1_answer_plan" };
        }
        if (s2) Object.assign(s2, { label: "Family and group bookings", slot: { ...s2.slot!, topic: "Family and group bookings", pageKind: null } });
        if (s3) Object.assign(s3, { label: "Catering prices", slot: { ...s3.slot!, topic: "Catering prices", pageKind: "existing" }, pages: [P("/catering", "todo", "To improve")] });
        G1.document = doc("doc_f1_answer_plan", "Answer Plan", "Client replied", at(wd(2), 14), {
            stateKey: "needs_us", waitingOn: "us", waitingSince: at(wd(2), 14), waitingWorkingDays: 2,
        });
        G1.extras = { pagesSold: G1.items.length, pagesLive: 0 };
        G1.history = [h(wd(2), "The client replied in the Answer Plan on the signature page", "reply", "Siew Ling", 14)];
        G1.docStep = g1CopyPackStep(G1);
    }
    const G2 = get(`G2-m${c}`);
    if (G2) {
        item(G2, "broken_4xx")!.pages = [P("/events/deepavali-2025", "todo", "Returned 404, new since the last scan")];
        item(G2, "broken_4xx")!.nextText = "Fix or redirect 1 broken page";
        item(G2, "redirect_chains")!.pages = [];
        item(G2, "canonicals")!.pages = [];
        G2.items.forEach((i) => (i.readAt = at(wd(3))));
        item(G2, "thin_content")!.pages = [P("/gallery", "todo", "96 words"), P("/booking", "todo", "88 words")];
    }
    const G3 = get(`G3-m${c}`);
    if (G3) {
        Object.assign(item(G3, "tick-scoreboard")!, { state: "done", proof: { kind: "link", text: "This month's scoreboard", url: "https://docs.google.com/", by: MANAGER, at: at(wd(1), 12) } });
        G3.history = [h(wd(1), "Scoreboard shared", "tick", MANAGER, 12)];
    }
    const G4 = get(`G4-m${c}`);
    if (G4) {
        // v1.3 (H31): this month's watch sits in a doc that is in the folder and not shared yet, so the next
        // step is to read it, then share it. Never "Create the doc" a second time.
        G4.docStep = { kind: "share", docId: "doc_f8_competitor_watch", docType: "Competitor watch", codes: ["G4"], outlet: null, expectedBy: G4.due };
        G4.history = [h(wd(2), "Competitor watch drafted in the doc, not shared yet", "plan", MANAGER, 14)];
    }
    const G5 = get(`G5-m${c}`);
    if (G5) {
        Object.assign(item(G5, "tick-small_change")!, { nextText: "Make the small change: opening hours on the contact page" });
        G5.history = [h(wd(1), "The client asked for a small change: opening hours on the contact page", "reply", "Siew Ling", 9)];
    }
    const G7 = get(`G7-m${c}`);
    if (G7) {
        Object.assign(item(G7, "record-reading")!, { state: "done", proof: { kind: "reading", text: "ChatGPT app reading stored", at: at(wd(2), 11) } });
        G7.history = [h(wd(2), "This month's ChatGPT app reading stored", "scan", null, 11)];
    }
}

/** nyonya-table's suggestions: one to decide, one YW dismissed in month 1 (S3's History says so). */
function sampleSuggestions(start: string): DeliverySuggestion[] {
    return [
        {
            id: "sug_contradiction_hours",
            rule: "contradiction",
            evidenceKey: "opening_hours",
            action: "Make the opening hours agree",
            why: "The contact page says 11am to 10pm and the Google profile says 11am to 9.30pm. ChatGPT quoted the Google hours in 3 of 50 answers.",
            evidence: { urls: ["/contact"] },
            state: "proposed",
        },
        {
            id: "sug_placeholder_pages",
            rule: "placeholder_pages",
            evidenceKey: "",
            action: "Delete 3 unfinished pages",
            why: "3 pages carry placeholder text and nothing else: /coming-soon, /test-page and /sample-page.",
            evidence: { urls: ["/coming-soon", "/test-page", "/sample-page"] },
            state: "dismissed",
            decidedBy: "YW",
            decidedAt: at(addDays(start, 14)),
        },
    ];
}

function suggestionsOf(s: BizState): SuggestionsPayload {
    const all = s.suggestions ?? [];
    return {
        assessmentId: null,
        proposals: all.filter((x) => x.state !== "dismissed"),
        decided: all.filter((x) => x.state !== "proposed").sort((a, b) => (b.decidedAt ?? "").localeCompare(a.decidedAt ?? "")),
    };
}

function sampleLedger(): LedgerRow[] {
    const business = getBusiness(SAMPLE_ID);
    const snapshot = getSnapshot(SAMPLE_ID);
    if (!business || !snapshot) return [];
    const tracker = getTracker(business, snapshot);
    const rows: LedgerRow[] = [];
    const rules: Array<[RegExp, LedgerRow["landsAs"], string | null, string]> = [
        [/admin access/i, "items", "S5", "S5: Website login given"],
        [/backup/i, "retired", null, "Retired by name: a safety habit, not a deliverable"],
        [/GSC|GA4|analytics/i, "items", "S5", "S5: Search Console connected; the analytics ask is retired by name"],
        [/six factor/i, "items", "A4", "A4: the scoreboard, from the baseline and the re-score"],
        [/naming baseline/i, "carried", "A1", "A1: the scored baseline run"],
        [/crawl audit/i, "carried", "S3", "S3: the first reading"],
        [/structured data/i, "items", "A2", "A2: one item per check on the key pages"],
        [/page labels/i, "items", "S1", "S1: one item per check on the key pages"],
        [/robots/i, "items", "A3", "A3: the robots file"],
        [/rewrite/i, "items", "B1", "B1: page slots"],
        [/question and answer/i, "moved", "B1", "B1's document"],
        [/press and media/i, "items", "B1", "B1: a kept press page takes a slot"],
        [/copy review/i, "moved", "B1", "B1's document"],
        [/Business Profile/i, "items", "S2", "S2: one tick per outlet"],
        [/name, address/i, "items", "S4", "S4: one tick per outlet"],
        [/Publish approved/i, "items", "B1", "B1: page slots"],
        [/sitemap and request recrawl/i, "moved", null, "The re-check (Check now)"],
        [/Re-run/i, "items", "A4", "A4: the re-score run"],
        [/scorecard|handover/i, "items", "A4", "A4: handover held, with the scorecard"],
        [/progress note|weekly note/i, "moved", null, "The Friday note"],
        [/fix order/i, "retired", null, "Retired by name"],
        [/named|Get .* on/i, "moved", null, "Visibility: suggestions per question"],
    ];
    const tasks = tracker.weeks.reduce<typeof tracker.weeks[number]["tasks"]>((all, w) => all.concat(w.tasks), []);
    tasks.forEach((t) => {
        const rule = rules.find(([re]) => re.test(t.action));
        const landsAs = rule ? rule[1] : "items";
        const final = t.status === "done" && landsAs === "items" ? "carried" : landsAs;
        rows.push({ step: t.action, code: rule ? rule[2] : null, landsAs: final, where: rule ? rule[3] : "Items under its code" });
    });
    return rows;
}

function ledgerCounts(rows: readonly LedgerRow[]): LedgerCounts {
    const n = (k: LedgerRow["landsAs"]) => rows.filter((r) => r.landsAs === k).length;
    return { total: rows.length, items: n("items"), carried: n("carried"), moved: n("moved"), outside: n("outside"), retired: n("retired"), unaccounted: n("unaccounted") };
}

/* ------------------------------------------------------------------------- *
 * One business: build, keep, compute
 * ------------------------------------------------------------------------- */

function ensure(businessId: string, e: Engagement): BizState {
    const sig = planSig(e);
    const have = store.businesses[businessId];
    if (have && have.planSig === sig) return seedWork(businessId, have);
    const start = (e.startedAt ?? today()).slice(0, 10);
    const current = e.startedAt ? termMonth(start, today(), e.termMonths) : 1;
    const fresh = buildDeliverables(businessId, e);
    if (have) {
        // A plan edit: keep what staff did on deliverables that still exist.
        const old = new Map(have.deliverables.map((d): [string, Deliverable] => [d.key, d]));
        fresh.forEach((d, i) => {
            const prev = old.get(d.key);
            if (prev) fresh[i] = { ...prev, label: d.label, window: d.window, due: d.due };
        });
        const next: BizState = { ...have, deliverables: fresh, planSig: sig };
        store.businesses[businessId] = next;
        save();
        return seedWork(businessId, next);
    }
    if (businessId === SAMPLE_ID) applyScenario(fresh, start, current);
    const state: BizState = {
        deliverables: fresh,
        keyPages: businessId === SAMPLE_ID ? sampleKeyPages() : { pages: [], suggested: [], candidates: [], confirmed: false },
        ledgerRows: businessId === SAMPLE_ID ? sampleLedger() : [],
        suggestions: businessId === SAMPLE_ID ? sampleSuggestions(start) : [],
        lastCheck: { at: businessId === SAMPLE_ID ? at(addDays(windowOf(start, 1).end, -5)) : null, kind: "scan", running: false },
        planSig: sig,
    };
    store.businesses[businessId] = state;
    save();
    return seedWork(businessId, state);
}

/** G1's step this month (lane 5): its Monthly Work Snapshot, not made yet. */
function g1CopyPackStep(G1: Deliverable): NonNullable<Deliverable["docStep"]> {
    return { kind: "create", docId: null, docType: "Monthly Work Snapshot", codes: ["G1"], outlet: null, expectedBy: G1.due };
}

/** A v4 store seeded before lane 5 has no G1 step: add it once (undefined, never a step a read cleared). */
function patchSampleSteps(businessId: string, s: BizState): void {
    if (businessId !== SAMPLE_ID) return;
    const G1 = s.deliverables.find((d) => d.code === "G1" && d.document?.docId === "doc_f1_answer_plan");
    if (!G1 || G1.docStep !== undefined) return;
    G1.docStep = g1CopyPackStep(G1);
    save();
}

/**
 * H31 on a read (v1.3): a stored doc step follows the client's docs as they
 * stand. A doc of the step's type covering its code (or the doc it names)
 * that the client can open clears it; one they cannot open makes it "Read
 * it, then share"; none leaves "Create the doc". Off without the write switch.
 */
function withDocStep(d: Deliverable, board: BusinessDocuments | null): Deliverable {
    const step = d.docStep;
    if (!step || !DOCS_WRITE_ON || !board) return d;
    const code = step.codes[0];
    const type = step.docType.toLowerCase();
    const docs = board.documents.filter(
        (doc) =>
            !doc.notClientDoc &&
            (doc.id === step.docId || ((doc.docType ?? "").toLowerCase() === type && doc.codes.includes(code) && (!step.outlet || doc.outlet === step.outlet))),
    );
    if (docs.some((doc) => doc.sharing.clientCanSee)) return { ...d, docStep: null };
    const unshared = docs[0] ?? null;
    // Create takes the catalogue's doc type for the code (the contract's create_doc), never a working title.
    return { ...d, docStep: unshared ? { ...step, kind: "share", docId: unshared.id } : { ...step, kind: "create", docId: null, docType: catalogueType(step.docType, code) } };
}

/** A step's doc type as the catalogue names it: its own when the catalogue has it, else the code's. */
function catalogueType(docType: string, code: string): string {
    return activeDocExpected().some((e) => e.docType === docType) ? docType : expectedDocFor(code)?.docType ?? docType;
}

/** The docs board the doc steps read, once per read; null where the docs sample has no such business. */
function boardFor(businessId: string): BusinessDocuments | null {
    if (!DOCS_WRITE_ON) return null;
    const b = docsStoreApi.business(businessId);
    return b ? docsStoreApi.board(b) : null;
}

function payloadOf(businessId: string, businessName: string, e: Engagement, s: BizState): DeliveryPayload {
    const t = today();
    const start = (e.startedAt ?? t).slice(0, 10);
    const month = e.startedAt ? termMonth(start, t, e.termMonths) : 1;
    const ctx = { today: t, manager: MANAGER, keyPagesConfirmed: s.keyPages.confirmed, docsWrite: DOCS_WRITE_ON };
    const board = s.deliverables.some((d) => d.docStep) ? boardFor(businessId) : null;
    const deliverables = s.deliverables.map((d) => computeDeliverable(withDocStep(d, board), ctx));
    const win = windowOf(start, month);
    const cameBack = deliverables.reduce((n, d) => n + d.items.filter((i) => i.cameBack && i.state === "todo").length, 0);
    return {
        businessId,
        businessName,
        asOf: now(),
        phase: !e.startedAt ? "not_started" : month === 1 ? "foundation" : "growth",
        month,
        monthLabel: month === 1 ? "Foundation, month 1" : `Month ${month}`,
        termMonths: e.termMonths,
        // Null before the plan starts, as the server sends it.
        due: e.startedAt ? lastWorkingDayOnOrBefore(win.end) : null,
        doneCount: doneCountOf(deliverables, month),
        cameBack,
        keyPages: { pages: s.keyPages.pages, confirmed: s.keyPages.confirmed, confirmedAt: s.keyPages.confirmedAt ?? null, confirmedBy: s.keyPages.confirmedBy ?? null },
        deliverables,
        ledger: ledgerCounts(s.ledgerRows),
        lastCheck: s.lastCheck,
        accountManager: MANAGER,
        nextCheck: at(addDays(t, 1), 2),
        documentWaits: documentWaitsOf(deliverables),
    };
}

/**
 * H13's list: each open deliverable's document that needs someone to move,
 * once per document, late first, then waiting on us, then on the client.
 */
function documentWaitsOf(list: readonly Deliverable[]): DocumentWait[] {
    const byDoc = new Map<string, DocumentWait>();
    list.forEach((d) => {
        const doc = d.document;
        if (!doc || d.group === "done" || d.group === "later") return;
        const stateKey = doc.stateKey;
        if (stateKey !== "late" && stateKey !== "needs_us" && stateKey !== "waiting_on_client") return;
        const have = byDoc.get(doc.docId);
        if (have) {
            if (!have.codes.includes(d.code)) have.codes.push(d.code);
            have.deliverableKeys.push(d.key);
            return;
        }
        const ask = d.items.find((i) => i.state === "waiting" && i.waitingOn === "client")?.clientAsk ?? null;
        byDoc.set(doc.docId, {
            docId: doc.docId,
            title: doc.title,
            codes: [d.code],
            outlet: null,
            stateKey,
            waitingOn: doc.waitingOn ?? null,
            waitingSince: doc.waitingSince ?? null,
            waitingWorkingDays: doc.waitingWorkingDays ?? null,
            late: Boolean(doc.late),
            lateWorkingDays: doc.lateWorkingDays ?? null,
            replyBy: doc.replyBy ?? null,
            expectedBy: null,
            question: doc.waitingOn === "client" && ask ? ask.replace(/\.$/, "").slice(0, 80) : null,
            repliedAt: doc.repliedAt ?? null,
            deliverableKeys: [d.key],
        });
    });
    const tier = (w: DocumentWait) => (w.late ? 0 : w.waitingOn === "us" ? 1 : w.waitingOn === "client" ? 2 : 3);
    return Array.from(byDoc.values()).sort(
        (a, b) => tier(a) - tier(b) || (b.lateWorkingDays ?? 0) - (a.lateWorkingDays ?? 0) || (a.waitingSince ?? a.expectedBy ?? "").localeCompare(b.waitingSince ?? b.expectedBy ?? ""),
    );
}

/* ------------------------------------------------------------------------- *
 * Finding things
 * ------------------------------------------------------------------------- */

function findItem(s: BizState, itemId: string): { d: Deliverable; item: DeliveryItem } | null {
    for (let k = 0; k < s.deliverables.length; k++) {
        const d = s.deliverables[k];
        const item = d.items.find((i) => i.id === itemId);
        if (item) return { d, item };
    }
    return null;
}

function computedOf(s: BizState, d: Deliverable): Deliverable {
    const businessId = Object.keys(store.businesses).find((k) => store.businesses[k] === s) ?? "";
    const stepped = d.docStep ? withDocStep(d, boardFor(businessId)) : d;
    return computeDeliverable(stepped, { today: today(), manager: MANAGER, keyPagesConfirmed: s.keyPages.confirmed, docsWrite: DOCS_WRITE_ON });
}

function log(d: Deliverable, text: string, area: HistoryEntry["area"], by: string | null = me()): void {
    d.history = [{ at: now(), text, area, by }, ...(d.history ?? [])];
}

function stateOf(businessId: string): BizState | null {
    return store.businesses[businessId] ?? null;
}

const notFound = () => deliveryFailure("notFound", { message: "That item is not on this client's Delivery." });
const rejected = (code: string, message: string) => deliveryFailure("rejected", { code, message });

/* ------------------------------------------------------------------------- *
 * The calls, as the service calls them
 * ------------------------------------------------------------------------- */

export const deliveryMock = {
    getDelivery(businessId: string, businessName: string, e: Engagement | null): Promise<DeliveryResult<DeliveryPayload | null>> {
        return respond(() => {
            if (!e) return { ok: true, data: null };
            const s = ensure(businessId, e);
            patchSampleSteps(businessId, s);
            return { ok: true, data: payloadOf(businessId, businessName, e, s) };
        });
    },

    markFixed(businessId: string, itemId: string, pages?: string[]): Promise<DeliveryResult<Deliverable>> {
        return respond(() => {
            const s = stateOf(businessId);
            const hit = s && findItem(s, itemId);
            if (!s || !hit) return notFound();
            const { d, item } = hit;
            if (item.kind !== "check") return rejected("not_a_check", "Only a check is marked fixed.");
            const want = pages && pages.length ? new Set(pages) : null;
            let n = 0;
            item.pages = item.pages.map((p) => {
                if (p.state !== "todo" || (want && !want.has(p.url))) return p;
                n += 1;
                return { ...p, state: "waiting", markedFixedAt: now(), cameBack: p.cameBack };
            });
            if (n === 0) return rejected("nothing_to_mark", "Every page is already marked or fixed.");
            item.markedFixedAt = now();
            item.markedFixedBy = me();
            log(d, `${me()} marked ${item.label.toLowerCase()} fixed on ${n === 1 ? "1 page" : `${n} pages`}`, "mark");
            return { ok: true, data: computedOf(s, d) };
        });
    },

    /** Undo a Mark fixed: the marked pages go back to To do. */
    unmarkFixed(businessId: string, itemId: string): Promise<DeliveryResult<Deliverable>> {
        return respond(() => {
            const s = stateOf(businessId);
            const hit = s && findItem(s, itemId);
            if (!s || !hit) return notFound();
            const { d, item } = hit;
            let n = 0;
            item.pages = item.pages.map((p) => {
                if (p.state !== "waiting" || !p.markedFixedAt) return p;
                n += 1;
                return { ...p, state: "todo", markedFixedAt: null };
            });
            if (n === 0) return rejected("nothing_to_undo", "Nothing marked fixed is waiting.");
            item.markedFixedAt = null;
            item.markedFixedBy = null;
            // Undo clears the paste list too: its ticks said it was pasted.
            if (item.pasteList) {
                item.pasteList.steps.forEach((x) => Object.assign(x, { ticked: false, tickedBy: null, tickedAt: null }));
                item.pasteList.allTicked = false;
            }
            log(d, `${me()} undid Mark fixed on ${item.label.toLowerCase()}`, "mark");
            return { ok: true, data: computedOf(s, d) };
        });
    },

    noAction(businessId: string, itemId: string, page: string, reason: string): Promise<DeliveryResult<Deliverable>> {
        return respond(() => {
            const s = stateOf(businessId);
            const hit = s && findItem(s, itemId);
            if (!s || !hit) return notFound();
            const why = reason.trim();
            if (!why) return rejected("reason_required", "Say why, in one line.");
            const { d, item } = hit;
            item.pages = item.pages.map((p) => (p.url === page ? { ...p, state: "no_action", noActionReason: why } : p));
            log(d, `No action on ${page}: ${why}`, "mark");
            return { ok: true, data: computedOf(s, d) };
        });
    },

    tick(businessId: string, itemId: string, body: TickIn): Promise<DeliveryResult<Deliverable>> {
        return respond(() => {
            const s = stateOf(businessId);
            const hit = s && findItem(s, itemId);
            if (!s || !hit) return notFound();
            const link = body.link?.trim() || null;
            const note = body.note?.trim() || null;
            if (!link && !note) return rejected("proof_required", "Add a link or a one-line note.");
            const { d, item } = hit;
            Object.assign(item, {
                state: "done",
                waitingOn: null,
                askedClientAt: null,
                clientRepliedAt: null,
                proof: { kind: link ? "link" : "note", text: note ?? "Link", url: link, by: me(), at: now() },
            });
            log(d, `${me()} ticked ${item.label.toLowerCase()}${note ? `: ${note}` : ""}`, "tick");
            return { ok: true, data: computedOf(s, d) };
        });
    },

    untick(businessId: string, itemId: string, reason?: string): Promise<DeliveryResult<Deliverable>> {
        return respond(() => {
            const s = stateOf(businessId);
            const hit = s && findItem(s, itemId);
            if (!s || !hit) return notFound();
            const { d, item } = hit;
            Object.assign(item, { state: "todo", proof: null, closedByHand: false, forcedDone: null });
            log(d, `${me()} undid the tick on ${item.label.toLowerCase()}${reason ? `: ${reason}` : ""}`, "tick");
            return { ok: true, data: computedOf(s, d) };
        });
    },

    forceItemDone(businessId: string, itemId: string): Promise<DeliveryResult<Deliverable>> {
        return respond(() => {
            const s = stateOf(businessId);
            const hit = s && findItem(s, itemId);
            if (!s || !hit) return notFound();
            const { d, item } = hit;
            if (item.state === "done") return rejected("already_done", "This item is already done.");
            const at = now();
            Object.assign(item, { state: "done", waitingOn: null, cameBack: false, closedByHand: true, forcedDone: { by: me(), at, whole: false }, proof: { kind: "by_hand", text: `Marked done by ${me()}`, url: null, by: me(), at } });
            log(d, `${item.label} marked done`, "mark", me());
            return { ok: true, data: computedOf(s, d) };
        });
    },

    forceDeliverableDone(businessId: string, key: string): Promise<DeliveryResult<Deliverable>> {
        return respond(() => {
            const s = stateOf(businessId);
            const d = s?.deliverables.find((x) => x.key === key);
            if (!s || !d) return notFound();
            if (computedOf(s, d).group === "done") return rejected("already_done", "This deliverable is already done.");
            const at = now();
            for (const item of d.items) {
                // Only what this press closes: an item already Done keeps its own proof, and Undo leaves it Done.
                if (item.state === "done") continue;
                Object.assign(item, { state: "done", waitingOn: null, cameBack: false, closedByHand: true, forcedDone: { by: me(), at, whole: true }, proof: { kind: "by_hand", text: `Marked done by ${me()}`, url: null, by: me(), at } });
            }
            d.forcedDone = { by: me(), at };
            log(d, "Marked done", "mark", me());
            return { ok: true, data: computedOf(s, d) };
        });
    },

    closeByHand(businessId: string, itemId: string, body: { link?: string; note: string; file?: File | null }): Promise<DeliveryResult<Deliverable>> {
        return respond(() => {
            const s = stateOf(businessId);
            const hit = s && findItem(s, itemId);
            if (!s || !hit) return notFound();
            const note = body.note.trim();
            if (!note) return rejected("proof_required", "Say how you know it is done, in one line.");
            const { d, item } = hit;
            item.pages = item.pages.map((p) => (p.state === "todo" || p.state === "not_read" || p.state === "waiting" ? { ...p, state: "closed_by_hand" } : p));
            Object.assign(item, { state: "done", closedByHand: true, waitingOn: null, proof: { kind: "by_hand", text: note, url: body.link?.trim() || null, by: me(), at: now() } });
            // H36: the file lives as long as this page on the sample; live, the server stores it.
            if (body.file) item.proofFile = { name: body.file.name, url: URL.createObjectURL(body.file), addedBy: me(), addedAt: now() };
            log(d, `${me()} closed ${item.label.toLowerCase()} by hand: ${note}${body.file ? `, with ${body.file.name}` : ""}`, "tick");
            return { ok: true, data: computedOf(s, d) };
        });
    },

    setPage(businessId: string, itemId: string, body: PageIn): Promise<DeliveryResult<Deliverable>> {
        return respond(() => {
            const s = stateOf(businessId);
            const hit = s && findItem(s, itemId);
            if (!s || !hit) return notFound();
            const url = body.url.trim();
            if (!url) return rejected("url_required", "Paste the page's live address.");
            if (body.pageKind === "existing" && !body.changed?.trim()) return rejected("changed_required", "Say what changed on the page, in one line.");
            const { d, item } = hit;
            const path = url.replace(/^https?:\/\/[^/]+/i, "") || "/";
            Object.assign(item, {
                state: "waiting",
                waitingOn: "oura",
                clientRepliedAt: null,
                slot: { ...(item.slot ?? { index: 1 }), pageKind: body.pageKind, changed: body.changed?.trim() || null },
                pages: [P(path, "waiting", body.pageKind === "new" ? "New page, Oura reads it tonight" : "Improved, Oura reads it tonight")],
                markedFixedAt: now(),
            });
            log(d, `${me()} added the live page ${path}`, "page");
            return { ok: true, data: computedOf(s, d) };
        });
    },

    setSlot(businessId: string, itemId: string, body: SlotIn): Promise<DeliveryResult<Deliverable>> {
        return respond(() => {
            const s = stateOf(businessId);
            const hit = s && findItem(s, itemId);
            if (!s || !hit) return notFound();
            const { d, item } = hit;
            const topic = body.topic?.trim();
            item.slot = { ...(item.slot ?? { index: 1 }), topic: topic ?? item.slot?.topic ?? null, pageKind: body.pageKind ?? item.slot?.pageKind ?? null };
            if (topic) item.label = topic;
            log(d, `${me()} set page ${item.slot.index}${topic ? ` to ${topic}` : ""}${body.pageKind ? `, ${body.pageKind}` : ""}`, "plan");
            return { ok: true, data: computedOf(s, d) };
        });
    },

    proof(businessId: string, key: string, body: ProofIn): Promise<DeliveryResult<Deliverable>> {
        return respond(() => {
            const s = stateOf(businessId);
            const d = s?.deliverables.find((x) => x.key === key);
            if (!s || !d) return notFound();
            if (!d.passedWithoutWork) return rejected("not_waiting_on_proof", "This one is not waiting on proof.");
            if (body.kind === "work") {
                const note = body.note.trim();
                if (!note) return rejected("proof_required", "Say what we did, in one line.");
                d.proofLine = note;
                log(d, `${me()} added what we did: ${note}${body.file ? `, with ${body.file.name}` : ""}`, "tick");
            } else {
                d.alreadyFine = { by: me(), at: now(), note: body.note?.trim() || null };
                d.proofLine = "Already fine before we started. Checked, not counted as our work.";
                log(d, `${me()} marked it already fine${body.note?.trim() ? `: ${body.note.trim()}` : ""}`, "tick");
            }
            d.passedWithoutWork = null;
            d.doneAt = today();
            return { ok: true, data: computedOf(s, d) };
        });
    },

    reopen(businessId: string, key: string, reason: string): Promise<DeliveryResult<Deliverable>> {
        return respond(() => {
            const s = stateOf(businessId);
            const d = s?.deliverables.find((x) => x.key === key);
            if (!s || !d) return notFound();
            const why = reason.trim();
            if (!why) return rejected("reason_required", "Say why it reopens, in one line.");
            const first = d.items[0];
            if (first) {
                Object.assign(first, { state: "todo", proof: null, closedByHand: false });
                first.pages = first.pages.map((p) => ({ ...p, state: "todo" }));
            }
            if (d.forcedDone) {
                // A whole line marked Done by hand (#6463): the reopen takes every item it closed back.
                for (const i of d.items) if (i.forcedDone?.whole) Object.assign(i, { state: "todo", proof: null, closedByHand: false, forcedDone: null });
                d.forcedDone = null;
            }
            d.doneAt = null;
            d.reopened = { reason: why, by: me(), at: now() };
            log(d, `${me()} reopened it: ${why}`, "plan");
            return { ok: true, data: computedOf(s, d) };
        });
    },

    checkNow(businessId: string): Promise<DeliveryResult<CheckNowResult>> {
        return respond(() => {
            const s = stateOf(businessId);
            if (!s) return notFound();
            if (s.lastCheck.running) return rejected("already_running", "A check is already running.");
            s.lastCheck = { ...s.lastCheck, running: true };
            const startedAt = now();
            // The sample's re-check takes a few seconds; live it is about a minute for a 600-page site.
            window.setTimeout(() => finishCheck(businessId), 4000);
            return { ok: true, data: { startedAt, etaSeconds: 60 } };
        });
    },

    /** H35: tick one paste step. The last tick marks the item fixed, so the same-day re-check reads it. */
    tickPasteStep(businessId: string, itemId: string, stepId: string, ticked: boolean): Promise<DeliveryResult<Deliverable>> {
        return respond(() => {
            const s = stateOf(businessId);
            const hit = s && findItem(s, itemId);
            if (!s || !hit) return notFound();
            const { d, item } = hit;
            const list = item.pasteList;
            const step = list?.steps.find((x) => x.id === stepId);
            if (!list || !step) return rejected("step_not_found", "That step is not on this item's paste list.");
            Object.assign(step, ticked ? { ticked: true, tickedBy: me(), tickedAt: now() } : { ticked: false, tickedBy: null, tickedAt: null });
            // The page a step changes moves with its tick: ticked, it waits on the re-check; unticked, it is to do again.
            const idx = list.steps.indexOf(step);
            const page = step.page ?? list.forDeveloper.split("\n")[idx]?.split(":")[0]?.trim() ?? null;
            if (page && item.pages.some((p) => p.url === page)) {
                if (ticked) markPagesFixed(item, page);
                else item.pages = item.pages.map((p) => (p.url === page && p.state === "waiting" && p.markedFixedAt ? { ...p, state: "todo", markedFixedAt: null } : p));
            }
            const wasAll = list.allTicked;
            list.allTicked = list.steps.every((x) => x.ticked);
            if (list.allTicked && !wasAll) {
                const n = markPagesFixed(item);
                if (item.kind === "check") Object.assign(item, { markedFixedAt: now(), markedFixedBy: me() });
                log(d, `${me()} pasted the last step: ${item.label.toLowerCase()} marked fixed${n ? ` on ${n === 1 ? "1 more page" : `${n} more pages`}` : ""}`, "mark");
            } else {
                log(d, `${me()} ${ticked ? "ticked" : "unticked"} a paste step: ${step.where}`, "mark");
            }
            return { ok: true, data: computedOf(s, d) };
        });
    },

    /**
     * H39 (D8): approved copy to the site. `draft_page`: an unpublished WordPress
     * draft page, where the connection allows; never published. `download`: the
     * approved copy as a file, where Oura cannot create a page (Wix, Framer).
     */
    copyToSite(businessId: string, itemId: string, mode: CopyToSiteMode): Promise<DeliveryResult<Deliverable>> {
        return respond(() => {
            const s = stateOf(businessId);
            const hit = s && findItem(s, itemId);
            if (!s || !hit) return notFound();
            const { d, item } = hit;
            const c = item.copyToSite;
            if (!c) return notFound();
            const page = item.pages[0]?.url ?? "/new-page";
            if (mode === "download") {
                const copy = businessId === SAMPLE_ID ? SIGNATURE_COPY : `${item.label}\n\nThe approved copy, from the client document.`;
                if (!c.downloadUrl) c.downloadUrl = `data:text/plain;charset=utf-8,${encodeURIComponent(copy)}`;
                // The paste list: one block per paragraph, a tick each, then Mark live for the next scan to confirm.
                if (!item.pasteList) {
                    const blocks = copy.split(/\n\s*\n/).map((b) => b.trim()).filter((b) => b && !/^Approved by /.test(b));
                    const where = PLATFORM_WORD[c.platform ?? "other"];
                    item.pasteList = {
                        platform: c.platform ?? "other",
                        steps: blocks.map((words, i) => ({
                            id: `copy-${i + 1}`,
                            where: i === 0 ? `${where}, then Pages, then ${page}, then the page heading` : `${where}, then Pages, then ${page}, then text box ${i}`,
                            words,
                            ticked: false,
                            tickedBy: null,
                            tickedAt: null,
                            page: null,
                        })),
                        allTicked: false,
                        forDeveloper: blocks.map((b, i) => `${page}, block ${i + 1}: ${b}`).join("\n"),
                    };
                }
                log(d, `${me()} got the approved copy for ${page} as a file, to paste in on the site`, "page");
                return { ok: true, data: computedOf(s, d) };
            }
            if (!c.allowed || c.mode !== "draft_page") return rejected("draft_page_not_allowed", c.reason ?? "Oura cannot make a draft on this site. Get the copy instead.");
            if (c.draftUrl) return rejected("draft_exists", "The draft is already on the site. Open it there.");
            const domain = getBusiness(businessId)?.domain ?? "example.com";
            c.draftUrl = `https://${domain}/wp-admin/edit.php?post_status=draft&post_type=page`;
            log(d, `${me()} made a draft page for ${page} on the site from the approved copy, not published`, "page");
            return { ok: true, data: computedOf(s, d) };
        });
    },

    /** H37: the "Live since" reply, once the person who clicked posted it in the doc through B20. */
    liveSincePosted(businessId: string, itemId: string): Promise<DeliveryResult<Deliverable>> {
        return respond(() => {
            const s = stateOf(businessId);
            const hit = s && findItem(s, itemId);
            if (!s || !hit) return notFound();
            const { d, item } = hit;
            if (!item.liveSinceDraft) return notFound();
            Object.assign(item.liveSinceDraft, { postedAt: now(), postedBy: me() });
            log(d, `${me()} posted the Live since reply in the doc`, "reply");
            return { ok: true, data: computedOf(s, d) };
        });
    },

    /** H38 (D7): the AI reader test, answering the checks. On the sample the robots fix is live, so all 3 get in and A3's readers item closes on it. */
    testAiReaders(businessId: string): Promise<DeliveryResult<ReaderCheck[]>> {
        return respond(() => {
            const s = stateOf(businessId);
            const t = today();
            const hit = s ? readerItemOf(s.deliverables, t) : null;
            if (!s || !hit) return notFound();
            const { d: A3, item } = hit;
            const robotsLive = A3.items.some((i) => i.checkKey === "robots_missing" && i.pages.every((p) => p.state !== "todo"));
            const checkedAt = now();
            item.readerChecks = READERS.map(([reader, agent]) => {
                const blocked = !robotsLive && reader === "claude";
                return { reader, agent, robotsAllows: !blocked, gotIn: blocked ? null : true, wordsVisible: blocked ? null : true, checkedAt };
            });
            const all = item.readerChecks.every((r) => r.robotsAllows && r.gotIn && r.wordsVisible);
            if (all) {
                Object.assign(item, { state: "done", waitingOn: null, nextText: null, proof: { kind: "scan", text: `All 3 AI readers get in, tested ${shortDay(t)}`, at: checkedAt } });
                log(A3, "AI reader test: all 3 get in, so the AI readers item closed", "scan", null);
            } else {
                Object.assign(item, { state: "todo", proof: null });
                log(A3, "AI reader test: ClaudeBot is still blocked by robots.txt", "scan", null);
            }
            return { ok: true, data: item.readerChecks };
        });
    },

    getKeyPages(businessId: string): Promise<DeliveryResult<KeyPages | null>> {
        return respond(() => {
            const s = stateOf(businessId);
            return { ok: true, data: s ? s.keyPages : null };
        });
    },

    putKeyPages(businessId: string, urls: string[]): Promise<DeliveryResult<KeyPages>> {
        return respond(() => {
            const s = stateOf(businessId);
            if (!s) return notFound();
            const clean = urls.map((u) => u.trim()).filter(Boolean);
            const unique = Array.from(new Set(clean));
            if (unique.length !== 10) return rejected("ten_pages", "Pick exactly 10 key pages.");
            const pool = new Map(s.keyPages.suggested.concat(s.keyPages.candidates, s.keyPages.pages).map((p): [string, KeyPage] => [p.url, p]));
            const was = new Set(s.keyPages.pages.map((p) => p.url));
            s.keyPages = {
                ...s.keyPages,
                pages: unique.map((u) => pool.get(u) ?? { url: u, source: "staff", title: null }),
                confirmed: true,
                confirmedAt: now(),
                confirmedBy: me(),
            };
            const swapped = unique.filter((u) => !was.has(u));
            const note = swapped.length ? `${me()} swapped in ${swapped.join(", ")} as key pages, from the next check` : `${me()} confirmed the 10 key pages`;
            s.deliverables.filter((d) => KEY_PAGE_CODES.has(d.code)).forEach((d) => log(d, note, "plan"));
            return { ok: true, data: s.keyPages };
        });
    },

    getSuggestions(businessId: string): Promise<DeliveryResult<SuggestionsPayload>> {
        return respond(() => {
            const s = stateOf(businessId);
            return { ok: true, data: s ? suggestionsOf(s) : { assessmentId: null, proposals: [], decided: [] } };
        });
    },

    /** Keep or dismiss one suggestion; History on the deliverable it maps to (H8's map). */
    decideSuggestion(businessId: string, suggestionId: string, decision: "keep" | "dismiss"): Promise<DeliveryResult<SuggestionsPayload>> {
        return respond(() => {
            const s = stateOf(businessId);
            const sg = s?.suggestions?.find((x) => x.id === suggestionId);
            if (!s || !sg) return deliveryFailure("rejected", { code: "suggestion_not_found", message: "This suggestion is not on the latest scan." });
            sg.state = decision === "keep" ? "kept" : "dismissed";
            sg.decidedBy = me();
            sg.decidedAt = now();
            const t = today();
    const month = s.deliverables.find((d) => !!d.window && d.window.start <= t && t <= d.window.end)?.month ?? 1;
            const code = sg.rule === "placeholder_pages" ? (month === 1 ? "S3" : "G2") : sg.rule === "press_page" ? (month === 1 ? "B1" : "G1") : "G5";
            const d = s.deliverables.find((x) => x.code === code && x.month === month) ?? s.deliverables.find((x) => x.code === code);
            if (d) log(d, `${decision === "keep" ? "Kept" : "Dismissed"}: ${sg.action}`, "plan");
            return { ok: true, data: suggestionsOf(s) };
        });
    },

    getLedger(businessId: string): Promise<DeliveryResult<{ counts: LedgerCounts; rows: LedgerRow[] }>> {
        return respond(() => {
            const s = stateOf(businessId);
            if (!s) return notFound();
            return { ok: true, data: { counts: ledgerCounts(s.ledgerRows), rows: s.ledgerRows } };
        });
    },

    getToday(clients: Array<{ businessId: string; businessName: string; engagement: Engagement | null }>): Promise<DeliveryResult<TodayPayload>> {
        return respond(() => {
            const t = today();
            const rows: TodayRow[] = [];
            const notSet: TodayPayload["notSet"] = [];
            clients.forEach(({ businessId, businessName, engagement }) => {
                if (!engagement) {
                    notSet.push({ businessId, businessName });
                    return;
                }
                const s = ensure(businessId, engagement);
                const p = payloadOf(businessId, businessName, engagement, s);
                p.deliverables.forEach((d, index) => {
                    if (d.group !== "needs_us" || !d.next || !d.actor) return;
                    const item = d.items.find((i) => i.id === d.next?.itemId);
                    // H28: came back, a client reply, late, or due by today. Work merely started is not today.
            const band = d.actor.cameBack || d.actor.late || Boolean(item?.clientRepliedAt) || (item?.due ?? d.due ?? "9999-12-31") <= t ? "today" : "coming";
                    rows.push({ businessId, businessName, deliverableKey: d.key, code: d.code, label: d.label, next: d.next, actor: d.actor, rank: todayRank(d, index), band });
                });
            });
            rows.sort((a, b) => a.rank - b.rank);
            return { ok: true, data: { asOf: now(), rows, notSet } };
        });
    },
};

/* ------------------------------------------------------------------------- *
 * Phase 3 on the sample (lane 6, contract v1.3)
 * ------------------------------------------------------------------------- */

const READERS = AI_READERS;

/** Mark a check's to-do pages fixed (or only `only`), as Mark fixed does. Returns how many. */
function markPagesFixed(item: DeliveryItem, only?: string): number {
    if (item.kind !== "check") return 0;
    let n = 0;
    item.pages = item.pages.map((p) => {
        if (p.state !== "todo" || (only && p.url !== only)) return p;
        n += 1;
        return { ...p, state: "waiting", markedFixedAt: now() };
    });
    if (n) Object.assign(item, { markedFixedAt: now(), markedFixedBy: me() });
    return n;
}

/**
 * H34 on the sample: a fix draft published in Site issues counts as Mark
 * fixed on its page, so the nightly or a Check now closes the item. Live,
 * the server does this when the publish lands.
 */
function publishSampleDraft(draftId: string, businessId = SAMPLE_ID): boolean {
    const s = stateOf(businessId);
    if (!s) return false;
    for (const d of s.deliverables) {
        for (const item of d.items) {
            const draft = item.drafts?.find((x) => x.id === draftId);
            if (!draft || draft.state !== "draft") continue;
            Object.assign(draft, { state: "published", publishedAt: now(), publishedBy: me() });
            const n = draft.kind === "fix" ? markPagesFixed(item, draft.page) : 0;
            log(d, `${me()} published the fix for ${draft.page}${n ? ", which counts as Mark fixed" : ""}`, "mark");
            save();
            emit();
            return true;
        }
    }
    return false;
}

if (typeof window !== "undefined" && DELIVERY_SAMPLE_SERVES) {
    // Dev tool: what Site issues' publish does to Delivery once H34 ships. `__ouraPublishSampleDraft("draft-fix-menu-markup")`.
    (window as unknown as Record<string, unknown>).__ouraPublishSampleDraft = publishSampleDraft;
}

/** A platform by its own name, in a paste step. */
const PLATFORM_WORD: Record<string, string> = { wix: "Wix", wordpress: "WordPress", framer: "Framer", webflow: "Webflow", squarespace: "Squarespace", other: "The site" };

/** The approved signature page copy, as the download serves it (sample words, nyonya-table only). */
const SIGNATURE_COPY = [
    "Nyonya Table tasting menu",
    "",
    "Eight courses of Peranakan cooking, served for the table to share.",
    "Our signature dishes: ayam buah keluak, nyonya laksa, chap chye and kueh pie tee. Book 2 days ahead for groups of 6 or more.",
    "",
    "Approved by Siew Ling in the Answer Plan.",
].join("\n");

/** The seed's version: 2 since G1's copy became a download on Wix; 3 since the usability round (robots impact line). */
const WORK_SEED = 3;

/** The usability round's additions, on a store seeded before it: A3's impact line. */
function patchUsabilityRound(s: BizState): void {
    for (const d of s.deliverables) {
        for (const i of d.items) {
            if (d.code === "A3" && i.checkKey === "robots_missing" && i.markedFixedAt && !i.impact) {
                i.impact = `ClaudeBot was blocked by robots.txt until the fix on ${shortDay(myDate(i.markedFixedAt))}, so Claude's answers could not read the site before then. ChatGPT and Perplexity were never blocked.`;
            }
        }
    }
}

/** Wix: Oura cannot create a page or a post there, so the approved copy is a file a person pastes in. */
const WIX_DOWNLOAD: CopyToSite = {
    mode: "download",
    allowed: false,
    platform: "wix",
    reason: "Oura cannot create pages on Wix. Get the copy and paste it in.",
    draftUrl: null,
    downloadUrl: null,
    docId: "doc_f1_answer_plan",
};

/**
 * nyonya-table's Phase 3 work, seeded once per store on top of the v4
 * scenario: fix drafts a person started (2 published, which counted as Mark
 * fixed; 1 still a draft), the signature page's draft from the client's
 * answer to Q3 with its approved copy as a download (Oura cannot create a page on Wix), and the AI reader test as it
 * read before the robots fix (ClaudeBot blocked), so Test again closes A3's
 * readers item on its proof.
 */
function seedWork(businessId: string, s: BizState): BizState {
    if (businessId !== SAMPLE_ID || s.workSeed === WORK_SEED) return s;
    if (s.workSeed === 1 || s.workSeed === 2) {
        // A store seeded before 2 Oct's fix round: Oura cannot create a page or a post on Wix, so G1's copy is a download.
        if (s.workSeed === 1) for (const d of s.deliverables) for (const i of d.items) if (i.copyToSite?.platform === "wix") i.copyToSite = { ...WIX_DOWNLOAD, docId: i.copyToSite.docId };
        patchUsabilityRound(s);
        s.workSeed = WORK_SEED;
        save();
        return s;
    }
    s.workSeed = WORK_SEED;
    const find = (code: string, part: string) => {
        const d = s.deliverables.find((x) => x.code === code && x.items.some((i) => i.id === `${x.key}:${part}`));
        return d ? { d, item: d.items.find((i) => i.id === `${d.key}:${part}`)! } : null;
    };

    const meta = find("S1", "missing_meta");
    if (meta) {
        const when = meta.item.markedFixedAt ?? at(wd(1), 16);
        meta.item.drafts = ["/", "/menu"].map((page, i) => ({
            id: `draft-fix-meta-${i + 1}`,
            kind: "fix" as const,
            page,
            label: `Summary line for ${page}`,
            state: "published" as const,
            startedBy: MANAGER,
            startedAt: at(wd(2), 11),
            publishedAt: when,
            publishedBy: MANAGER,
            approvalKey: "S1:missing_meta",
        }));
    }
    const schema = find("A2", "no_schema");
    if (schema) {
        schema.item.drafts = [
            { id: "draft-fix-menu-markup", kind: "fix", page: "/menu", label: "Restaurant markup for /menu", state: "draft", startedBy: MANAGER, startedAt: at(wd(1), 10), approvalKey: "A2:no_schema" },
        ];
    }
    const headings = find("S1", "missing_h1");
    if (headings?.item.pasteList) {
        headings.item.pasteList.forDeveloper = [
            "Nyonya Table: main headings (H1) to set, from ourteam",
            "",
            ...headings.item.pasteList.steps.map((st, i) => `${i + 1}. ${st.where}: ${st.words ?? ""}`),
        ].join("\n");
    }

    const g1 = s.deliverables.filter((x) => x.code === "G1" && x.document?.docId === "doc_f1_answer_plan")[0];
    const sig = g1?.items[0];
    if (sig?.drafts?.[0]) {
        Object.assign(sig.drafts[0], { fromQuestionId: "q_f1_3", fromQuestionNumber: 3 });
        sig.copyToSite = WIX_DOWNLOAD;
    }

    const readers = find("A3", "tick-ai_readers");
    if (readers) {
        const ran = at(wd(2), 2);
        readers.item.readerChecks = READERS.map(([reader, agent]) =>
            reader === "claude"
                ? { reader, agent, robotsAllows: false, gotIn: null, wordsVisible: null, checkedAt: ran }
                : { reader, agent, robotsAllows: true, gotIn: true, wordsVisible: true, checkedAt: ran },
        );
        Object.assign(readers.item, { state: "todo", proof: null, nextText: "Test the AI readers again once the robots file is live" });
        readers.d.history = [h(wd(2), "AI reader test: ClaudeBot blocked by robots.txt", "scan", null, 2), ...readers.d.history];
    }
    save();
    return s;
}

/** Oura's re-check, finishing: every page waiting on a check is read again. */
function finishCheck(businessId: string): void {
    const s = stateOf(businessId);
    if (!s) return;
    const t = now();
    let cleared = 0;
    s.deliverables.forEach((d) => {
    if (!d.window || d.window.start > today()) return;
        let touched = false;
        d.items.forEach((item) => {
            if (item.kind === "check") {
                let resolved = 0;
                item.pages = item.pages.map((p) => {
                    if (p.state === "waiting") {
                        resolved += 1;
                        return { ...p, state: "done", detail: p.detail ?? "Clear on the re-check", cameBack: false };
                    }
                    // PageSpeed now measures every key page it can load (H14); one it cannot stays unread.
                    if (p.state === "not_read" && item.checkKey === "cwv" && !/could not load/i.test(p.detail ?? "")) {
                        resolved += 1;
                        return { ...p, state: "done", detail: "Measured on the re-check, passes" };
                    }
                    return p;
                });
                // The re-check reads the check itself: no failing page is now a real clear.
                const wasUnread = !item.readAt;
                item.readAt = t;
                if (resolved === 0 && !(item.pages.length === 0 && wasUnread)) return;
                touched = true;
                cleared += resolved;
                const settled = settleItem(item);
                if (settled.state === "done") item.proof = { kind: "scan", text: `Clear on the re-check ${shortDay(today())}`, at: t };
                Object.assign(item, { state: settled.state, counts: settled.counts, cameBack: settled.cameBack });
            } else if (item.kind === "page" && item.state === "waiting") {
                touched = true;
                cleared += 1;
                Object.assign(item, {
                    state: "done",
                    waitingOn: null,
                    pages: item.pages.map((p) => ({ ...p, state: "done", detail: item.slot?.pageKind === "new" ? "New page, read by the re-check" : "Improved, read by the re-check" })),
                    proof: { kind: "live", text: `Live, read ${shortDay(today())}`, at: t },
                });
            }
        });
        if (touched) log(d, "Re-check: the marked pages read clear", "scan", null);
    });
    s.lastCheck = { at: t, kind: "recheck", running: false };
    save();
    emit();
    void cleared;
}

/* ------------------------------------------------------------------------- *
 * For the merchant sample and other screens
 * ------------------------------------------------------------------------- */

/** The computed deliverables for a business already in the store, or null. Synchronous, for the merchant sample. */
export function sampleDeliverables(businessId: string, e: Engagement | null): Deliverable[] | null {
    if (!DELIVERY_SAMPLE_SERVES || !e) return null;
    const s = ensure(businessId, e);
    const ctx = { today: today(), manager: MANAGER, keyPagesConfirmed: s.keyPages.confirmed, docsWrite: DOCS_WRITE_ON };
    return s.deliverables.map((d) => computeDeliverable(d, ctx));
}

/** Group order, re-exported for screens that sort. */
export const DELIVERY_GROUP_ORDER = GROUP_ORDER;
export { dayLabel as deliveryDayLabel };
